// Axios instance and types
import http from '../api'
import { APIResponse, APIPaginatedResponse, Pagination } from '../types'
import { TypeSale, TypeSaleItem } from '../../types/Sale'
import { parseDate, toFiniteNumberOrZero } from '../utils/utils'
import { saleItemLineDiscountAmount } from '../../utils/lineDiscount'
import { TypeInvoice, TypeInvoiceFilters } from '../../types/Invoice'

const apiPath = import.meta.env.VITE_API_PATH

type InvoiceStaffMemberPayload = {
  uuid: string
  commission_type?: number
  commission_value?: number
  amount?: number
}

type InvoiceItemPayload = {
  item_uuid: string
  quantity: number
  unit_price: number
  total_price: number
  comment?: string | null
  tax_rate?: number
  tax_amount?: number
  line_discount_amount?: number
  invoice_discount_share_amount?: number
  loyalty_share_amount?: number
  net_amount?: number
  service_point_id?: number
  staff_members?: InvoiceStaffMemberPayload[]
}

/** POST /api/invoices body — keys must match backend payload validation exactly. */
type InvoiceRequestPayload = {
  sale_uuid: string
  client_uuid: string | null
  invoice_date: string
  total_amount: number
  total_amount_payable: number
  loyalty_points?: number
  loyalty_amount?: number
  discount_type?: number
  discount_rate?: number
  discount_amount?: number
  tax_type?: number
  tax_amount?: number
  tax_rate?: number
  notes?: string | null
  invoice_items: InvoiceItemPayload[]
}

const buildInvoiceStaffMember = (
  member: NonNullable<TypeSaleItem['staff_members']>[number],
): InvoiceStaffMemberPayload => {
  const staffMember: InvoiceStaffMemberPayload = {
    uuid: member.uuid,
    commission_value: toFiniteNumberOrZero(member.commission_value),
    amount: toFiniteNumberOrZero(member.amount),
  }

  if (member.commission_type !== undefined && member.commission_type !== null) {
    staffMember.commission_type = member.commission_type
  }

  return staffMember
}

const buildInvoiceItem = (item: TypeSaleItem): InvoiceItemPayload => {
  const invoiceItem: InvoiceItemPayload = {
    item_uuid: item.item_uuid,
    quantity: toFiniteNumberOrZero(item.quantity),
    unit_price: toFiniteNumberOrZero(item.unit_price),
    total_price: toFiniteNumberOrZero(item.total_price),
  }

  if (item.comment) {
    invoiceItem.comment = item.comment
  }

  if (item.service_point_id != null) {
    invoiceItem.service_point_id = item.service_point_id
  }

  const staffMembers = (item.staff_members || []).map(buildInvoiceStaffMember)
  if (staffMembers.length > 0) {
    invoiceItem.staff_members = staffMembers
  }

  const lineDiscountAmount = saleItemLineDiscountAmount(item)
  if (lineDiscountAmount > 0) {
    invoiceItem.line_discount_amount = lineDiscountAmount
  }

  return invoiceItem
}

const buildInvoicePayload = (sale: TypeSale): InvoiceRequestPayload => {
  if (!sale.uuid) {
    throw new Error('Sale UUID is required to create an invoice.')
  }

  const clientUuid = sale.client_uuid || null
  const isWalkIn = !clientUuid

  return {
    sale_uuid: sale.uuid,
    // Empty string fails Laravel `uuid` validation; walk-ins must send null.
    client_uuid: clientUuid,
    invoice_date: sale.date ? parseDate('' + sale.date) : parseDate(new Date().toISOString()),
    total_amount: toFiniteNumberOrZero(sale.total_sale_amount),
    total_amount_payable: toFiniteNumberOrZero(sale.total_sale_amount_payable),
    invoice_items: sale.sale_items.map(buildInvoiceItem),
    loyalty_points: isWalkIn ? 0 : Math.max(0, Math.trunc(toFiniteNumberOrZero(sale.loyalty_points))),
    loyalty_amount: isWalkIn ? 0 : toFiniteNumberOrZero(sale.loyalty_amount),
    discount_type: sale.discount_type,
    discount_rate: toFiniteNumberOrZero(sale.discount_value),
    discount_amount: toFiniteNumberOrZero(sale.discount_amount),
    tax_type: sale.tax_type,
    tax_amount: toFiniteNumberOrZero(sale.tax_amount),
    tax_rate: toFiniteNumberOrZero(sale.tax_value),
    notes: sale.comment ?? null,
  }
}

async function create(sale: TypeSale) {
  const payload = buildInvoicePayload(sale)
  return await http.post<APIResponse<any>>(apiPath + 'invoices', payload, { errorHandle: false } as any)
}

type InvoiceLookupConfig = { errorHandle?: boolean; timeout?: number; signal?: AbortSignal }

async function getInvoiceByUuid(invoiceUuid: string, config?: InvoiceLookupConfig) {
  return await http.get<APIResponse<TypeInvoice>>(
    `${apiPath}invoices/${encodeURIComponent(invoiceUuid)}`,
    config as any,
  )
}

async function getInvoiceByNo(invoiceNo: string, config?: InvoiceLookupConfig) {
  return await http.get<APIResponse<TypeInvoice>>(
    `${apiPath}invoices/number/${encodeURIComponent(invoiceNo)}`,
    config as any,
  )
}

async function getInvoiceBySaleNo(saleNo: string) {
  return await http.get<APIResponse<any>>(`${apiPath}invoices/sale-no/${saleNo}`)
}

async function searchInvoices(filters: TypeInvoiceFilters, pagination?: Pagination) {
  const params = new URLSearchParams()

  if (filters.invoice_number) {
    params.append('invoice_no', filters.invoice_number)
  }
  if (filters.sale_no) {
    params.append('sale_no', filters.sale_no)
  }
  if (filters.client_name) {
    params.append('client_name', filters.client_name)
  }
  if (filters.client_uuid) {
    params.append('client_uuid', filters.client_uuid)
  }
  if (filters.payment_status !== undefined) {
    params.append('payment_status', filters.payment_status.toString())
  }
  if (filters.start_date) {
    params.append('start_date', filters.start_date)
  }
  if (filters.end_date) {
    params.append('end_date', filters.end_date)
  }

  // Add pagination parameters using standard format
  if (pagination?.page) {
    params.append('page', pagination.page.toString())
  }
  if (pagination?.perPage) {
    params.append('per_page', pagination.perPage.toString())
  }

  return await http.get<APIResponse<APIPaginatedResponse<any>>>(`${apiPath}invoices?${params.toString()}`)
}

export default {
  create,
  getInvoiceByUuid,
  getInvoiceByNo,
  searchInvoices,
  getInvoiceBySaleNo,
}
